import { FC } from "react";
import {
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";
import DefaultContactForm, {
  ContactFormData,
} from "../Forms/DefaultContactForm";
import { Contact } from "../contacts.types";
import ContactsAPI from "@/api/contacts";
import { useSnackbar, VariantType } from "notistack";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  extractCustomFieldsFromFormData,
  processCustomAttributesForCreation,
} from "../Forms/utils/customAttributeUtils";

interface CreateContactFlyoutProps {
  open: boolean;
  onSubmit: (contact: Contact) => void;
  handleClose: () => void;
  caseInfo?: { case_id: number } | null;
}

const CreateContactFlyout: FC<CreateContactFlyoutProps> = ({
  open,
  onSubmit,
  handleClose,
  caseInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  if (!open) return null;

  const handleSubmit = async (data: ContactFormData) => {
    try {
      const customAttributes = processCustomAttributesForCreation(data);
      const cleanedFormData = extractCustomFieldsFromFormData(data);
      const payload = {
        ...cleanedFormData,
        case_id: caseInfo?.case_id,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      const createdContact = await ContactsAPI.createContact(payload);

      const newContact: Contact = {
        ...cleanedFormData,
        ...createdContact,
        client_id: createdContact.client_id,
        isContact: 1,
      };

      // Pass the created contact data back to the parent
      onSubmit(newContact);

      enqueueSnackbar(`Contact ${data?.name || ""} created`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created Contact: ${data?.name}`,
        event: "contact:create",
        object_type: "contact",
      });

      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <Flyout open={open} onClose={handleClose}>
      <FlyoutHeader>
        <FlyoutTitle>Create Contact</FlyoutTitle>
      </FlyoutHeader>
      <div style={{ padding: "20px", flex: 1, overflow: "auto" }}>
        <DefaultContactForm
          onSubmit={handleSubmit}
          onCancel={handleClose}
          buttonProps={{ submitText: "Create Contact" }}
          isFlyout={true}
        />
      </div>
    </Flyout>
  );
};

export default CreateContactFlyout;
